import React from 'react'
import { Box, InputBase, IconButton, useTheme } from '@material-ui/core'
import styled from '@emotion/styled'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import useAutoComplete from 'src/components/chatbot/hooks/useAutoComplete'
import AutoCompleteMenu from 'src/components/chatbot/components/AutoCompleteMenu'
import useSettings from 'src/hooks/useSettings'
import SelectionChips from 'src/components/chatbot/components/SelectionChips'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import useChat from 'src/components/chatbot/hooks/useChat'
// import { useState, useEffect } from 'react'

const StyledInputBase = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.neutral,
  borderRadius: 20,
  padding: theme.spacing(1, 2),
  width: '100%',
}))

const StyledInput = styled(InputBase)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0.75, 0),
  textAlign: 'center',
  width: '100%',
  '&::placeholder': {
    textAlign: 'center',
  },
}))

const MessageInput = React.memo(
  ({
    selectedHPOTerms = [],
    onRemoveHPOTerm,
    selectedDiseases = [],
    onRemoveDisease,
    selectedSamples = [],
    onRemoveSample,
    selectedPanels = [],
    onRemovePanel,
    isWaitingForBotAnswer,
  }) => {
    const autoComplete = useAutoComplete()
    const { themeMode } = useSettings()
    const isDarkMode = themeMode === 'dark'
    const theme = useTheme()
    const { nameToId, diseaseNameToIdMapping } = useChatbotStore()
    const { handleSendMessage } = useChat()

    const handleSend = () => {
      // Build selected chips data first to check if we have any chips
      const selectedChipsData = {}

      if (selectedHPOTerms.length > 0) {
        selectedChipsData.hpoTerms = selectedHPOTerms
      }

      if (selectedDiseases.length > 0) {
        selectedChipsData.diseases = selectedDiseases
      }

      if (selectedSamples.length > 0) {
        selectedChipsData.samples = selectedSamples
      }

      if (selectedPanels.length > 0) {
        selectedChipsData.panels = selectedPanels
      }

      const hasSelectedChips = Object.keys(selectedChipsData).length > 0
      const hasTextInput = autoComplete.inputValue.trim()

      // Don't send if there's no text input and no selected chips
      if (!hasTextInput && !hasSelectedChips) return

      // Use default message when only chips are selected
      const originalMessage = hasTextInput ? autoComplete.inputValue : 'We have these as context:'
      let contextAppendedMessage = originalMessage

      if (selectedHPOTerms.length > 0) {
        const hpoText = selectedHPOTerms.map((term) => `hpo:${nameToId.hpo[term.name]}`).join(', ')
        contextAppendedMessage += `\n Selected HPO terms: ${hpoText}`
      }

      if (selectedDiseases.length > 0) {
        const diseaseItems = selectedDiseases
          .map((disease) => {
            const omimFromSelection = disease?.omim || disease?.id
            if (omimFromSelection) return `disease:${omimFromSelection}`

            const name = disease?.disease || disease?.name
            const mappedId = (name && diseaseNameToIdMapping?.[name]) || null
            if (!mappedId) {
              console.log('MessageInput ERROR: selected disease missing OMIM id and mapping; skipping', disease)
              return null
            }
            return `disease:${mappedId}`
          })
          .filter(Boolean)
        if (diseaseItems.length > 0) {
          contextAppendedMessage += `\n Selected diseases: ${diseaseItems.join(', ')}`
        }
      }

      if (selectedSamples.length > 0) {
        const samplesText = selectedSamples.map((sample) => `sample:${nameToId.sample[sample.name]}`).join(', ')
        contextAppendedMessage += `\n Selected samples: ${samplesText}`
      }

      if (selectedPanels.length > 0) {
        const panelsText = selectedPanels.map((panel) => `panel:${nameToId.panel[panel.name]}`).join(', ')
        contextAppendedMessage += `\n Selected panels: ${panelsText}`
      }

      // we seperate the message that is rendered and the actual message sent to the backend.
      // so the one rendered doesn't show the appended context text!
      handleSendMessage({
        displayMessage: originalMessage,
        backendMessage: contextAppendedMessage,
        selectedChips: hasSelectedChips ? selectedChipsData : null,
      })
      autoComplete.setInputValue('')
    }

    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !autoComplete.triggerPosition) {
        e.preventDefault()
        handleSend()
      }
    }

    // Check if we can send (either has text input or selected chips)
    const canSend =
      autoComplete.inputValue.trim() ||
      selectedHPOTerms.length > 0 ||
      selectedDiseases.length > 0 ||
      selectedSamples.length > 0 ||
      selectedPanels.length > 0

    return (
      <Box sx={{ width: '100%' }}>
        <SelectionChips
          selectedHPOTerms={selectedHPOTerms}
          onRemoveHPOTerm={onRemoveHPOTerm}
          selectedDiseases={selectedDiseases}
          onRemoveDisease={onRemoveDisease}
          selectedSamples={selectedSamples}
          onRemoveSample={onRemoveSample}
          selectedPanels={selectedPanels}
          onRemovePanel={onRemovePanel}
        />

        <StyledInputBase theme={theme} isDarkMode={isDarkMode}>
          <StyledInput
            theme={theme}
            placeholder="Ask anything"
            value={autoComplete.inputValue}
            onChange={(e) => autoComplete.setInputValue(e.target.value)}
            onKeyDown={autoComplete.handleKeyDown}
            onKeyPress={handleKeyPress}
            inputProps={{ 'aria-label': 'message input' }}
            inputRef={autoComplete.inputRef}
            fullWidth
          />
          <IconButton size="small" color="primary" onClick={handleSend} disabled={!canSend || isWaitingForBotAnswer}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>

          <AutoCompleteMenu
            anchorEl={autoComplete.inputRef.current}
            open={!!autoComplete.triggerPosition}
            suggestions={autoComplete.suggestions}
            selectedIndex={autoComplete.selectedIndex}
            onSelect={autoComplete.handleSelection}
            onClose={autoComplete.closeMenu}
            isLoading={autoComplete.isLoading}
          />
        </StyledInputBase>
      </Box>
    )
  },
)

export default MessageInput
